import React, { useEffect, useRef } from "react";

const FOCUSABLE_SELECTOR = [
  "a[href]",
  "button:not([disabled])",
  "textarea:not([disabled])",
  "input:not([disabled])",
  "select:not([disabled])",
  "[tabindex]:not([tabindex='-1'])",
].join(",");

const getFocusableElements = (container) => {
  if (!container) {
    return [];
  }

  return Array.from(container.querySelectorAll(FOCUSABLE_SELECTOR))
    .filter((element) => !element.hasAttribute("disabled") && element.getAttribute("aria-hidden") !== "true");
};

const getInitialFocusTarget = (container, initialFocusSelector) => {
  const focusableElements = getFocusableElements(container);

  if (!focusableElements.length) {
    return null;
  }

  if (initialFocusSelector) {
    const preferredElement = container.querySelector(initialFocusSelector);

    if (focusableElements.includes(preferredElement)) {
      return preferredElement;
    }
  }

  return focusableElements[0];
};

const ApplicationInviteSideModal = ({
  isOpen,
  onClose,
  closeDisabled = false,
  titleId,
  panelClassName,
  panelTestId,
  dataRecordId,
  initialFocusSelector,
  children,
}) => {
  const panelRef = useRef(null);
  const onCloseRef = useRef(onClose);
  const closeDisabledRef = useRef(closeDisabled);
  const previousActiveElementRef = useRef(null);

  useEffect(() => {
    onCloseRef.current = onClose;
    closeDisabledRef.current = closeDisabled;
  }, [closeDisabled, onClose]);

  useEffect(() => {
    if (!isOpen) {
      return undefined;
    }

    previousActiveElementRef.current = document.activeElement;
    getInitialFocusTarget(panelRef.current, initialFocusSelector)?.focus();

    const handleKeyDown = (event) => {
      if (event.key === "Escape") {
        if (!closeDisabledRef.current) {
          onCloseRef.current?.();
        }
        return;
      }

      if (event.key !== "Tab") {
        return;
      }

      const panelElement = panelRef.current;
      const focusableElements = getFocusableElements(panelElement);

      if (!focusableElements.length) {
        event.preventDefault();
        return;
      }

      const firstElement = focusableElements[0];
      const lastElement = focusableElements[focusableElements.length - 1];
      const activeElement = document.activeElement;
      const isFocusOutsidePanel = panelElement && !panelElement.contains(activeElement);

      if (event.shiftKey && (activeElement === firstElement || isFocusOutsidePanel)) {
        event.preventDefault();
        lastElement.focus();
        return;
      }

      if (!event.shiftKey && (activeElement === lastElement || isFocusOutsidePanel)) {
        event.preventDefault();
        firstElement.focus();
      }
    };

    document.addEventListener("keydown", handleKeyDown);

    return () => {
      document.removeEventListener("keydown", handleKeyDown);

      const previousActiveElement = previousActiveElementRef.current;
      if (previousActiveElement && document.contains(previousActiveElement)) {
        previousActiveElement.focus?.();
      }

      previousActiveElementRef.current = null;
    };
  }, [initialFocusSelector, isOpen]);

  if (!isOpen) {
    return null;
  }

  return (
    <div
      className="invite-member-side-modal"
      role="presentation"
      data-testid="invite-member-modal"
    >
      <div className="invite-member-side-modal__backdrop" />
      <aside
        ref={panelRef}
        className={panelClassName}
        role="dialog"
        aria-modal="true"
        aria-labelledby={titleId}
        data-testid={panelTestId}
        data-record-id={dataRecordId || undefined}
      >
        {children}
      </aside>
    </div>
  );
};

export default ApplicationInviteSideModal;
